import React, { useState, useEffect, useMemo } from 'react';
import {
  createTrilogyByCoordinates,
  createTrilogyByObject,
  createTrilogyImage
} from '../../api/images';
import { CodePopup } from '../CodeSnippet';
import { FormInput, FormSelect, FormFileSelect, FormGroup } from './FormComponents';

// Reusable Dashboard card for consistent styling
const DashboardCard = ({ title, children, className }) => {
  return (
    <div className={`p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors ${className}`}>
      <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
        {title}
      </h2>
      {children}
    </div>
  );
};

const TrilogyTool = ({ collectionId, availableFiles, prefilledData }) => {
  // Search mode: 'coordinates' | 'object' | 'files'
  const [searchMode, setSearchMode] = useState('coordinates');
  const [ra, setRa] = useState('');
  const [dec, setDec] = useState('');
  const [coordinates, setCoordinates] = useState(''); // Combined RA, Dec input
  const [size, setSize] = useState('5.0');
  const [sizeUnit, setSizeUnit] = useState('arcmin');
  const [objectName, setObjectName] = useState('');

  // Parse coordinates from combined input
  const parseCoordinates = (coordStr) => {
    if (!coordStr) return { ra: '', dec: '' };
    // Support formats: "150.5, -23.4" or "150.5 -23.4"
    const parts = coordStr.split(/[,\s]+/).filter(p => p.trim());
    if (parts.length >= 2) {
      return { ra: parts[0].trim(), dec: parts[1].trim() };
    }
    return { ra: '', dec: '' };
  };

  // Handle combined coordinate input
  const handleCoordinatesChange = (value) => {
    setCoordinates(value);
    const { ra: parsedRa, dec: parsedDec } = parseCoordinates(value);
    setRa(parsedRa);
    setDec(parsedDec);
  };

  // Handle prefilled data from ImageBrowser
  useEffect(() => {
    if (prefilledData) {
      if (prefilledData.ra_center !== undefined && prefilledData.dec_center !== undefined) {
        const raVal = prefilledData.ra_center.toString();
        const decVal = prefilledData.dec_center.toString();
        setRa(raVal);
        setDec(decVal);
        setCoordinates(`${raVal}, ${decVal}`);
      }
      if (prefilledData.object_name) {
        setObjectName(prefilledData.object_name);
      }
      // Set search mode to coordinates when prefilled
      setSearchMode('coordinates');
    }
  }, [prefilledData]);
  
  // Trilogy-specific parameters
  const [noiselum, setNoiselum] = useState(0.15);
  const [satpercent, setSatpercent] = useState(0.15);
  const [colorsatfac, setColorsatfac] = useState(2.0);
  
  // Filters (multiple selection) for each channel
  const [rFilters, setRFilters] = useState([]);
  const [gFilters, setGFilters] = useState([]);
  const [bFilters, setBFilters] = useState([]);
  
  // Files (multiple selection) for each channel (store file IDs)
  const [rFileIds, setRFileIds] = useState([]);
  const [gFileIds, setGFileIds] = useState([]);
  const [bFileIds, setBFileIds] = useState([]);
  
  // File search and pattern fields
  const [pattern, setPattern] = useState('');
  const [rFileSearchText, setRFileSearchText] = useState('');
  const [gFileSearchText, setGFileSearchText] = useState('');
  const [bFileSearchText, setBFileSearchText] = useState('');
  const [rFilteredFiles, setRFilteredFiles] = useState([]);
  const [gFilteredFiles, setGFilteredFiles] = useState([]);
  const [bFilteredFiles, setBFilteredFiles] = useState([]);
  
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trilogyImageData, setTrilogyImageData] = useState(null);
  const [codePopup, setCodePopup] = useState({ isOpen: false, snippetName: '', dynamicData: {} });
  
  // Get a sorted array of unique filters from available files
  const uniqueFilters = useMemo(() => {
    if (!availableFiles || availableFiles.length === 0) return [];
    const filterSet = new Set();
    availableFiles.forEach(file => {
      if (file.filter) filterSet.add(file.filter);
    });
    return Array.from(filterSet).sort();
  }, [availableFiles]);
  
  // Update filtered files for each channel
  useEffect(() => {
    if (!availableFiles) {
      setRFilteredFiles([]);
      setGFilteredFiles([]);
      setBFilteredFiles([]);
      return;
    }
    
    // Red channel files
    if (rFileSearchText) {
      const lowerSearch = rFileSearchText.toLowerCase();
      setRFilteredFiles(availableFiles.filter(file => {
        return file.filename?.toLowerCase().includes(lowerSearch) || 
               file.filter?.toLowerCase().includes(lowerSearch) || 
               file.object_name?.toLowerCase().includes(lowerSearch);
      }));
    } else {
      setRFilteredFiles(availableFiles);
    }
    
    // Green channel files
    if (gFileSearchText) {
      const lowerSearch = gFileSearchText.toLowerCase();
      setGFilteredFiles(availableFiles.filter(file => {
        return file.filename?.toLowerCase().includes(lowerSearch) || 
               file.filter?.toLowerCase().includes(lowerSearch) || 
               file.object_name?.toLowerCase().includes(lowerSearch);
      }));
    } else {
      setGFilteredFiles(availableFiles);
    }
    
    // Blue channel files
    if (bFileSearchText) {
      const lowerSearch = bFileSearchText.toLowerCase();
      setBFilteredFiles(availableFiles.filter(file => {
        return file.filename?.toLowerCase().includes(lowerSearch) || 
               file.filter?.toLowerCase().includes(lowerSearch) || 
               file.object_name?.toLowerCase().includes(lowerSearch);
      }));
    } else {
      setBFilteredFiles(availableFiles);
    }
  }, [availableFiles, rFileSearchText, gFileSearchText, bFileSearchText]);
  
  // Handle mode changes: reset error and previous result
  const handleSearchModeChange = (newMode) => {
    setSearchMode(newMode);
    setError('');
    setTrilogyImageData(null);
  };
  
  // Form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    setTrilogyImageData(null);
    try {
      let result;
      const options = {
        size: parseFloat(size),
        sizeUnit,
        noiselum: parseFloat(noiselum),
        satpercent: parseFloat(satpercent),
        colorsatfac: parseFloat(colorsatfac)
      };
      
      switch (searchMode) {
        case 'coordinates':
          if (!ra || !dec) {
            throw new Error('Please provide RA and Dec coordinates');
          }
          if (rFilters.length === 0) {
            throw new Error('Please select at least one filter for the red channel');
          }
          if (gFilters.length === 0) {
            throw new Error('Please select at least one filter for the green channel');
          }
          if (bFilters.length === 0) {
            throw new Error('Please select at least one filter for the blue channel');
          }
          result = await createTrilogyByCoordinates(
            collectionId,
            parseFloat(ra),
            parseFloat(dec),
            parseFloat(size),
            rFilters,
            gFilters,
            bFilters,
            {
              ...options,
              pattern: pattern || undefined
            }
          );
          break;
        case 'object':
          if (!objectName) {
            throw new Error('Please provide an object name');
          }
          if (rFilters.length === 0) {
            throw new Error('Please select at least one filter for the red channel');
          }
          if (gFilters.length === 0) {
            throw new Error('Please select at least one filter for the green channel');
          }
          if (bFilters.length === 0) {
            throw new Error('Please select at least one filter for the blue channel');
          }
          // Optionally include RA/Dec if provided
          if (ra && dec) {
            options.ra = parseFloat(ra);
            options.dec = parseFloat(dec);
          }
          result = await createTrilogyByObject(
            collectionId,
            objectName,
            rFilters,
            gFilters,
            bFilters,
            {
              ...options,
              pattern: pattern || undefined
            }
          );
          break;
        case 'files':
          if (rFileIds.length === 0 || gFileIds.length === 0 || bFileIds.length === 0) {
            throw new Error('Please select at least one file for each channel');
          }
          // Optionally include region cutout coordinates
          if (ra && dec) {
            options.ra = parseFloat(ra);
            options.dec = parseFloat(dec);
          }
          result = await createTrilogyImage(
            rFileIds,
            gFileIds,
            bFileIds,
            options
          );
          break;
        default:
          throw new Error('Invalid search mode');
      }
      setTrilogyImageData(result);
    } catch (err) {
      console.error('Error creating Trilogy image:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create Trilogy image');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <DashboardCard title="Trilogy RGB Image Tool" className="mt-3">
      <p className="mb-4 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
        Create multi-band RGB images using the Trilogy algorithm. Unlike standard RGB, each channel can combine multiple filters.
      </p>
      
      {/* Tab Navigation */}
      <div className="flex border-b border-ui-light-border dark:border-ui-dark-border mb-4">
        <button
          onClick={() => handleSearchModeChange('coordinates')}
          className={`px-4 py-2 -mb-px font-medium text-sm focus:outline-none ${searchMode === 'coordinates'
            ? 'border-b-2 border-primary-600 text-ui-light-text-primary dark:text-ui-dark-text-primary'
            : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'}`}
        >
          By Coordinates
        </button>
        <button
          onClick={() => handleSearchModeChange('object')}
          className={`px-4 py-2 -mb-px font-medium text-sm focus:outline-none ${searchMode === 'object'
            ? 'border-b-2 border-primary-600 text-ui-light-text-primary dark:text-ui-dark-text-primary'
            : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'}`}
        >
          By Object
        </button>
        <button
          onClick={() => handleSearchModeChange('files')}
          className={`px-4 py-2 -mb-px font-medium text-sm focus:outline-none ${searchMode === 'files'
            ? 'border-b-2 border-primary-600 text-ui-light-text-primary dark:text-ui-dark-text-primary'
            : 'text-ui-light-text-secondary dark:text-ui-dark-text-tertiary'}`}
        >
          By Files
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* For Coordinates mode */}
        {searchMode === 'coordinates' && (
          <div className="space-y-4">
            <FormInput
              label="Coordinates (RA, Dec)"
              value={coordinates}
              onChange={(e) => handleCoordinatesChange(e.target.value)}
              placeholder="e.g., 150.5, -23.4 or 150.5 -23.4"
              helpText="Enter coordinates as: RA (0-360°), Dec (-90 to 90°)"
              required
            />
            <FormGroup>
              <FormInput
                label="Size"
                type="number"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                placeholder="Enter size"
                step="any"
                required
                className="flex-1"
              />
              <FormSelect
                label="Unit"
                value={sizeUnit}
                onChange={(e) => setSizeUnit(e.target.value)}
                options={[
                  { value: 'arcmin', label: 'arcmin' },
                  { value: 'arcsec', label: 'arcsec' },
                  { value: 'pixels', label: 'pixels' }
                ]}
                className="w-32"
              />
            </FormGroup>
            <FormInput
              label="Filename Pattern (optional)"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="Enter pattern to match filenames (e.g. *.fits)"
            />
          </div>
        )}
        
        {/* For Object mode */}
        {searchMode === 'object' && (
          <div className="space-y-4">
            <FormInput
              label="Object Name"
              value={objectName}
              onChange={(e) => setObjectName(e.target.value)}
              placeholder="Enter object name"
              required
            />
            <FormGroup>
              <FormInput
                label="Size"
                type="number"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                placeholder="Enter size (optional)"
                step="any"
                className="flex-1"
              />
              <FormSelect
                label="Unit"
                value={sizeUnit}
                onChange={(e) => setSizeUnit(e.target.value)}
                options={[
                  { value: 'arcmin', label: 'arcmin' },
                  { value: 'arcsec', label: 'arcsec' },
                  { value: 'pixels', label: 'pixels' }
                ]}
                className="w-32"
              />
            </FormGroup>
            <div>
              <p className="text-sm text-ui-light-text-muted dark:text-ui-dark-text-muted mb-2">
                Optional: Provide coordinates to override those from the database.
              </p>
              <FormInput
                label="Coordinates (RA, Dec)"
                value={coordinates}
                onChange={(e) => handleCoordinatesChange(e.target.value)}
                placeholder="e.g., 150.5, -23.4 or 150.5 -23.4 (optional)"
                helpText="Optional: Enter coordinates as: RA (0-360°), Dec (-90 to 90°)"
              />
            </div>
            <FormInput
              label="Filename Pattern (optional)"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="Enter pattern to match filenames (e.g. *.fits)"
            />
          </div>
        )}
        
        {/* For Files mode */}
        {searchMode === 'files' && (
          <div className="space-y-6">
            <FormInput
              label="Coordinates (RA, Dec)"
              value={coordinates}
              onChange={(e) => handleCoordinatesChange(e.target.value)}
              placeholder="e.g., 150.5, -23.4 or 150.5 -23.4 (optional)"
              helpText="Optional: Enter coordinates as: RA (0-360°), Dec (-90 to 90°)"
            />
            <FormGroup>
              <FormInput
                label="Size"
                type="number"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                placeholder="Enter size (optional)"
                step="any"
                className="flex-1"
              />
              <FormSelect
                label="Unit"
                value={sizeUnit}
                onChange={(e) => setSizeUnit(e.target.value)}
                options={[
                  { value: 'arcmin', label: 'arcmin' },
                  { value: 'arcsec', label: 'arcsec' },
                  { value: 'pixels', label: 'pixels' }
                ]}
                className="w-32"
              />
            </FormGroup>
            <div>
              <p className="text-sm text-ui-light-text-muted dark:text-ui-dark-text-muted">
                Note: File selection is for direct image generation.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
                Select Files for Each Channel
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Red Channel Files', value: rFileIds, setter: setRFileIds, searchText: rFileSearchText, setSearchText: setRFileSearchText, filteredFiles: rFilteredFiles },
                  { label: 'Green Channel Files', value: gFileIds, setter: setGFileIds, searchText: gFileSearchText, setSearchText: setGFileSearchText, filteredFiles: gFilteredFiles },
                  { label: 'Blue Channel Files', value: bFileIds, setter: setBFileIds, searchText: bFileSearchText, setSearchText: setBFileSearchText, filteredFiles: bFilteredFiles }
                ].map((channel) => (
                  <div key={channel.label} className="space-y-2">
                    <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
                      {channel.label}
                    </label>
                    {availableFiles && availableFiles.length > 0 ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder={`Search ${channel.label.split(' ')[0]} files`}
                          className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                          value={channel.searchText}
                          onChange={(e) => channel.setSearchText(e.target.value)}
                        />
                        <select
                          multiple
                          value={channel.value}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                            channel.setter(selected);
                          }}
                          className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                          required
                          size="5"
                        >
                          {channel.filteredFiles.map(file => (
                            <option key={file.id} value={file.id}>
                              {file.filename} ({file.filter || 'No filter'})
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <input
                        type="text"
                        placeholder="No files available"
                        disabled
                        className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-gray-100 text-ui-light-text-secondary"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Filter selection for Coordinates and Object modes */}
        {(searchMode === 'coordinates' || searchMode === 'object') && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
              Select Filters for Each Channel
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
                  Red Channel Filters
                  <span className="text-status-error-light-text dark:text-status-error-dark-text ml-1">*</span>
                </label>
                <select
                  multiple
                  value={rFilters}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                    setRFilters(selected);
                  }}
                  className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  required
                >
                  {uniqueFilters.map(filter => (
                    <option key={filter} value={filter}>
                      {filter}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
                  Green Channel Filters
                  <span className="text-status-error-light-text dark:text-status-error-dark-text ml-1">*</span>
                </label>
                <select
                  multiple
                  value={gFilters}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                    setGFilters(selected);
                  }}
                  className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  required
                >
                  {uniqueFilters.map(filter => (
                    <option key={filter} value={filter}>
                      {filter}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
                  Blue Channel Filters
                  <span className="text-status-error-light-text dark:text-status-error-dark-text ml-1">*</span>
                </label>
                <select
                  multiple
                  value={bFilters}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                    setBFilters(selected);
                  }}
                  className="block w-full px-3 py-2.5 border border-ui-light-border dark:border-ui-dark-border rounded-xl bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  required
                >
                  {uniqueFilters.map(filter => (
                    <option key={filter} value={filter}>
                      {filter}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
        
        {/* Advanced Options using a details element */}
        <details className="mt-6 border border-ui-light-border dark:border-ui-dark-border rounded-xl">
          <summary className="cursor-pointer p-2 text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
            Advanced Options
          </summary>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormInput
              label="Noise Level"
              type="number"
              value={noiselum}
              onChange={(e) => setNoiselum(e.target.value)}
              step="0.01"
              min="0"
              max="1"
              helpText="Controls faint pixel visibility (0-1)"
            />
            <FormInput
              label="Saturation Percentage"
              type="number"
              value={satpercent}
              onChange={(e) => setSatpercent(e.target.value)}
              step="0.01"
              min="0"
              max="100"
              helpText="Percentage of pixels allowed to saturate (0-100)"
            />
            <FormInput
              label="Color Saturation Factor"
              type="number"
              value={colorsatfac}
              onChange={(e) => setColorsatfac(e.target.value)}
              step="0.1"
              min="0"
              max="10"
              helpText="Enhances color contrast (1-10)"
            />
          </div>
        </details>
        
        {/* Error Message */}
        {error && (
          <div className="p-3 mt-4 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
            {error}
          </div>
        )}
        
        {/* Submit Button */}
        <div className="mt-6 flex justify-center gap-2">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            {loading ? (
              <svg className="w-5 h-5 inline animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            ) : 'Create Trilogy Image'}
          </button>
          <button
            type="button"
            onClick={() => {
              const snippetName = searchMode === 'coordinates' ? 'trilogy_by_coordinates' :
                                searchMode === 'object' ? 'trilogy_by_object' : 'trilogy_by_files';
              setCodePopup({
                isOpen: true,
                snippetName,
                dynamicData: {
                  base_url: window.location.origin,
                  collection_id: collectionId,
                  ra: ra || '0.0',
                  dec: dec || '0.0',
                  size: size || '1.0',
                  size_unit: sizeUnit,
                  r_filters: JSON.stringify(rFilters),
                  g_filters: JSON.stringify(gFilters),
                  b_filters: JSON.stringify(bFilters),
                  noiselum: noiselum,
                  satpercent: satpercent,
                  colorsatfac: colorsatfac,
                  pattern: pattern || '',
                  object_name: objectName || '',
                  r_file_ids: JSON.stringify(rFileIds),
                  g_file_ids: JSON.stringify(gFileIds),
                  b_file_ids: JSON.stringify(bFileIds)
                }
              });
            }}
            className="px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-hover dark:bg-ui-dark-hover rounded-md hover:bg-ui-light-card dark:hover:bg-ui-dark-card focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors flex items-center gap-2"
            title="Show Python code"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Code
          </button>
        </div>
      </form>
      
      {/* Result Display */}
      {trilogyImageData && (
        <div className="mt-8">
          <hr className="my-4 border-ui-light-border dark:border-ui-dark-border" />
          <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-4">
            Trilogy RGB Image Result
          </h3>
          <div className="flex justify-center">
            <img
              src={URL.createObjectURL(trilogyImageData.data)}
              alt="Trilogy RGB composite"
              className="max-w-full max-h-[500px]"
            />
          </div>
          <h4 className="mt-4 text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
            Metadata:
          </h4>
          <pre className="overflow-auto max-h-48 bg-ui-light-card dark:bg-ui-dark-card p-4 rounded-md text-xs text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
            {JSON.stringify(trilogyImageData.metadata, null, 2)}
          </pre>
        </div>
      )}

      {/* Code Popup */}
      <CodePopup
        isOpen={codePopup.isOpen}
        onClose={() => setCodePopup({ isOpen: false, snippetName: '', dynamicData: {} })}
        snippetName={codePopup.snippetName}
        dynamicData={codePopup.dynamicData}
      />
    </DashboardCard>
  );
};

export default TrilogyTool;